import React, { useEffect, useRef, useState } from "react"
import ReactSVG from "react-svg"
import Logo from "../../../images/CarAdvise-Logo.svg"
import MagicLinkLockError from "../../../images/magic-link/magic-link-lock-error.svg"
import SessionInfoLogo from "../../../images/session-info-image.svg"
import { Button, Image } from "semantic-ui-react"
import {
  EXACT_NUMERIC_REGEX,
  EXPIRATION_DURATION_TYPE,
  ONLY_NUMERIC_REGEX,
  OTP_LENGTH,
  PASSCODE_EXPIRED_TIME,
  PASSCODE_EXPIRED_TIME_EBAY,
  TEXT,
  TIMER_KEY,
  TYPE,
  VALUE
} from "../../../v1/constants/otp-auth"
import { BACKSPACE } from "../../../v1/constants/application"
import { useTranslation } from "react-i18next"
import InfoLogo from "../../../images/info-strock.svg"
import GoBackLogo from "../../../images/rewards/back.svg"
import { Redirect, withRouter } from "react-router-dom"
import { routeChange, routerPaths } from "../../../v1/constants/paths"
import { requestOtp } from "../../../v1/actions/otp-auth"
import { connect } from "react-redux"
import { formatPhoneNumber } from "../../../v1/helpers/stringHelpers"
import { OPTIONS } from "../../constants/common"
import { signIn } from "../../../v1/actions/user"
import store from "../../utils/configureStore"
import Notifications from "../../../v1/components/Notifications"
import { getSubdomain } from "../../../v1/components/Config/StylesheetInjector"
import { EBAY } from "../../../v1/constants/affiliations"

const EMPTY_OTP = new Array(OTP_LENGTH).fill("")

const OtpVerification = ({ isLoggedIn, user, history, location, requestOtp, signIn }) => {
  const { t } = useTranslation("otp")

  const params = new URLSearchParams(location.search)
  const { type: authType, value: authValue, generatedAt } = location.state || {}

  const inputRefs = useRef([])
  const timerIdRef = useRef(null)

  const [otp, setOtp] = useState(EMPTY_OTP)
  const [isInvalidOtp, setIsInvalidCode] = useState(false)
  const [isVerificationLoading, setIsVerificationLoading] = useState(false)
  const [isOptGenerating, setOptGenerating] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [errorMessage, setErrorMessage] = useState("")
  const [hasRequstedForOTP, setHasRequstedForOTP] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)

  useEffect(() => {
    let timer
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1)
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [timeLeft])

  useEffect(() => {
    startTimer(generatedAt)
  }, []) // Empty array to run only once

  const handleChange = (value, index) => {
    if (ONLY_NUMERIC_REGEX.test(value)) {
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)

      // Move focus to the next input box if the current one is filled
      if (value && index < OTP_LENGTH - 1) {
        if (inputRefs.current[index + 1]) {
          inputRefs.current[index + 1].focus()
        }
      }
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === BACKSPACE) {
      if (!otp[index] && index > 0) {
        if (inputRefs.current[index - 1]) {
          inputRefs.current[index - 1].focus()
        }
      }
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData(TEXT).slice(0, OTP_LENGTH)
    const newOtp = [...otp]

    for (let i = 0; i < pastedData.length; i++) {
      if (EXACT_NUMERIC_REGEX.test(pastedData[i])) {
        newOtp[i] = pastedData[i]
      }
    }

    setOtp(newOtp)

    // Auto-focus to the first empty input
    const firstEmptyIndex = newOtp.findIndex((val) => val === "")

    if (firstEmptyIndex !== -1 && inputRefs.current[firstEmptyIndex]) {
      inputRefs.current[firstEmptyIndex].focus()
    }
  }

  const handleOtpVerification = async () => {
    setIsVerificationLoading(true)

    try {
      setIsInvalidCode(false)
      const res = await signIn({
        otp: otp.join(""),
        otpType: authType,
        otpTypeValue: authValue,
        isOtpVerification: true,
        callback: (type, error) => {
          setIsInvalidCode(true)
          setErrorMessage(error.message.message)
        }
      })
    } catch (error) {
      console.log("Error in handleOtpVerification", error)
    } finally {
      setIsVerificationLoading(false)
    }
  }

  const handleResendOtp = async () => {
    setOtp(EMPTY_OTP)
    setIsInvalidCode(false)
    setOptGenerating(true)
    setHasRequstedForOTP(true)

    try {
      const response = await requestOtp({
        type: authType,
        value: authValue
      })
      if (response.error) {
        store.dispatch(
          Notifications.error({
            title: t("settings:errorTitle"),
            message: response.error,
            position: "tr",
            autoDismiss: 5,
            action: {
              label: "Dismiss"
            }
          })
        )
        return
      }
      startTimer()
    } catch (error) {
      console.log("Error in handleResendOtp", error)
    } finally {
      setOptGenerating(false)
    }
  }

  const disabledSubmitButton = otp.some((digit) => digit === "")

  if (isLoggedIn && user && user.id) {
    return <Redirect to={routerPaths.dashboard} />
  }

  const startTimer = (dateString) => {
    const startTime = dateString ? new Date(dateString).getTime() : Date.now()
    const endTime = startTime + 30 * 60 * 1000 // 30 minutes after startTime
    const now = Date.now()

    const remainingTime = Math.max(0, Math.floor((endTime - now) / 1000)) // Convert ms to seconds
    setTimeLeft(remainingTime)
  }

  const formatTime = () => {
    const mins = Math.floor(timeLeft / 60)
    const secs = timeLeft % 60
    return `${mins} min ${secs < 10 ? "0" : ""}${secs} seconds`
  }

  return (
    <div className="magic-link-container">
      <div className="magic-link-left-container responsive-large-container">
        <ReactSVG src={SessionInfoLogo} className="logo-image" />
      </div>
      <div className="magic-link-right-container">
        <div className="responsive-small-magic-link">
          <div className="header-logo">
            <img src={Logo} />
          </div>
        </div>
        <div className="contentCenter">
          <Image className="lock-image" src={MagicLinkLockError} />
          <div className="otp-verification">
            <p className="otp-verification-code-sent">
              {t("enterDigit", {
                digitLength: OTP_LENGTH
              })}
            </p>
            <p className="otp-verification-email-phone">
              {authType === OPTIONS.PHONE ? formatPhoneNumber(authValue) : authValue}
            </p>
            <p className="otp-verification-expiration">
              {t("passwordExpiration", {
                passwordExpirationTime:
                  getSubdomain() === EBAY ? PASSCODE_EXPIRED_TIME_EBAY : PASSCODE_EXPIRED_TIME
              })}
            </p>
            <div className="otp-input-container">
              {otp.map((value, index) => (
                <input
                  ref={(el) => (inputRefs.current[index] = el)}
                  key={index}
                  type="text"
                  className={`otp-input ${isInvalidOtp ? "invalid-otp" : ""}`}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="1"
                  value={value}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={(e) => index === 0 && handlePaste(e)}
                />
              ))}
            </div>

            {isInvalidOtp && (
              <div className="otp-verification-failed">
                <ReactSVG wrapper="span" className="info-logo" src={InfoLogo} />
                <span className="otp-verification-error">{errorMessage || t("invalidCode")}</span>
              </div>
            )}
            <Button
              className={`otp-verification-continue ${
                disabledSubmitButton ? "otp-verification-disabled" : ""
              }`}
              disabled={disabledSubmitButton || isVerificationLoading}
              onClick={handleOtpVerification}
              loading={isVerificationLoading}
            >
              {t("continue")}
            </Button>

            {!!secondsLeft && (
              <div className="otp-verification-code-request">
                <p className="otp-time-label">{t("codeRequest")}</p>
                <p>
                  {secondsLeft} {EXPIRATION_DURATION_TYPE}
                </p>
              </div>
            )}
            {hasRequstedForOTP && (
              <Button
                disabled={isOptGenerating || isVerificationLoading || timeLeft > 0}
                onClick={handleResendOtp}
                className="otp-verification-resend"
              >
                {t("resendCode")}
                {timeLeft > 0 && " in " + formatTime()}
              </Button>
            )}
            {!hasRequstedForOTP && (
              <Button
                disabled={isOptGenerating || isVerificationLoading}
                onClick={() => {
                  setHasRequstedForOTP(true)
                }}
                className="otp-verification-resend"
              >
                {t("didntGetOtp")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => {
  let user = state.user || {}

  return {
    user,
    isLoggedIn: !!user.authentication_token,
    isLoading: !!user.loading
  }
}

export default withRouter(connect(mapStateToProps, { requestOtp, signIn })(OtpVerification))
